const S3_HOST_PATTERN = /^[^.]+\.s3(?:\.[^.]+)?\.amazonaws\.com$/i;

const shouldUseLocalS3Proxy = () => {
  if (typeof window === 'undefined') return false;
  const { hostname } = window.location;
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

export const toProxiedS3Url = (url) => {
  if (!url || typeof url !== 'string') return url;
  if (url.startsWith('/s3-proxy/')) return url;
  if (!shouldUseLocalS3Proxy()) return url;

  // Prefer URL parsing over fragile regex (Sonar: RegExp.exec / empty-group smells).
  const httpsMatch = /^https:\/\//i.exec(url);
  if (!httpsMatch) return url;
  const rest = url.slice(httpsMatch[0].length);
  const slashIdx = rest.search(/[/?#]/);
  const host = slashIdx === -1 ? rest : rest.slice(0, slashIdx);
  if (!host || !S3_HOST_PATTERN.test(host)) return url;
  const pathAndQuery = slashIdx === -1 ? '' : rest.slice(slashIdx);
  return `/s3-proxy/${host}${pathAndQuery}`;
};

export const fetchScanReport = async (reportUrl) => {
  const response = await fetch(toProxiedS3Url(reportUrl));
  if (!response.ok) {
    throw new Error(`Failed to load scan report (${response.status})`);
  }
  return response.json();
};

export const buildGridLayoutFromTiles = (tiles) => {
  if (!tiles?.length) return null;
  const rows = Math.max(...tiles.map(({ row }) => row)) + 1;
  const cols = Math.max(...tiles.map(({ col }) => col)) + 1;
  return {
    rows,
    cols,
    tiles: tiles.map((tile) => (
      tile?.url ? { ...tile, url: toProxiedS3Url(tile.url) } : tile
    )),
  };
};

export const MASK_POINT_COUNT = 4;

export const screenPointToNormalized = (clientX, clientY, element) => {
  if (!element) return null;
  const rect = element.getBoundingClientRect();
  if (!rect.width || !rect.height) return null;
  return {
    x: Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)),
    y: Math.min(1, Math.max(0, (clientY - rect.top) / rect.height)),
  };
};

export const pointsToSvgPolygon = (points) => (
  points.map(({ x, y }) => `${x},${y}`).join(' ')
);

/** Cross product orientation: >0 left turn, <0 right turn, 0 collinear. */
const orient = (a, b, c) => (
  (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x)
);

const onSegment = (a, b, c) => (
  Math.min(a.x, b.x) <= c.x && c.x <= Math.max(a.x, b.x)
  && Math.min(a.y, b.y) <= c.y && c.y <= Math.max(a.y, b.y)
);

/** Proper or improper intersection of segments AB and CD (shared endpoints ignored). */
const segmentsIntersect = (a, b, c, d) => {
  const o1 = orient(a, b, c);
  const o2 = orient(a, b, d);
  const o3 = orient(c, d, a);
  const o4 = orient(c, d, b);

  if (o1 === 0 && onSegment(a, b, c)) return false;
  if (o2 === 0 && onSegment(a, b, d)) return false;
  if (o3 === 0 && onSegment(c, d, a)) return false;
  if (o4 === 0 && onSegment(c, d, b)) return false;

  return (o1 > 0) !== (o2 > 0) && (o3 > 0) !== (o4 > 0);
};

/**
 * True when a closed polygon's non-adjacent edges cross (e.g. bow-tie quad).
 * Triangles and fewer points never self-intersect for our purposes.
 */
export const polygonSelfIntersects = (points) => {
  const n = points?.length ?? 0;
  if (n < 4) return false;

  for (let i = 0; i < n; i += 1) {
    const a = points[i];
    const b = points[(i + 1) % n];
    for (let j = i + 1; j < n; j += 1) {
      const sharesVertex = (i + 1) % n === j || (j + 1) % n === i;
      if (!sharesVertex) {
        const c = points[j];
        const d = points[(j + 1) % n];
        if (segmentsIntersect(a, b, c, d)) return true;
      }
    }
  }
  return false;
};

export const createMaskFromPoints = (points) => ({
  id: String(Date.now()),
  points: points.map(({ x, y }) => ({ x, y })),
});

export const masksEqual = (a, b) => JSON.stringify(a || []) === JSON.stringify(b || []);
